import { type ComponentProps, useMemo } from "react";
import { Link } from "react-router";
import type { WikiLinkTarget } from "@/api";
import { Markdown, type MarkdownComponents, type MarkdownRemarkPlugins } from "@/components/chat/Markdown";
import { MARKDOWN_LINK_CLASS, MarkdownLink } from "@/components/chat/MarkdownMedia";
import { isWikiFileHref, remarkWikilinks, stripFrontmatter, wikiFileHref } from "./wikilinks";

/** In-app wiki links navigate client-side; everything else behaves like chat links. */
function WikiAnchor(props: Readonly<ComponentProps<"a"> & { node?: unknown }>) {
  // Streamdown passes target="_blank" + rel for every link: wrong for an in-app link.
  const { href, children, node: _node, target: _target, rel: _rel, ...rest } = props;
  if (isWikiFileHref(href)) {
    return (
      <Link {...rest} to={href} className={MARKDOWN_LINK_CLASS} data-streamdown="link">
        {children}
      </Link>
    );
  }
  return <MarkdownLink {...props} />;
}

const COMPONENTS: MarkdownComponents = { a: WikiAnchor };

/**
 * A wiki page rendered: frontmatter hidden, `[[links]]` turned into in-app
 * links to the pages the backend resolved them to. Links the reader can't
 * follow (unresolved, or into pages they can't see) render as plain text, so
 * the two are indistinguishable.
 */
export function WikiMarkdown({ content, links }: Readonly<{ content: string; links: WikiLinkTarget[] }>) {
  const plugins = useMemo<MarkdownRemarkPlugins>(() => {
    const byText = new Map(links.map((link) => [link.text, wikiFileHref(link.spaceUuid, link.path)]));
    return [[remarkWikilinks, { resolve: (target: string) => byText.get(target) ?? null }]];
  }, [links]);
  return (
    <Markdown remarkPlugins={plugins} components={COMPONENTS}>
      {stripFrontmatter(content)}
    </Markdown>
  );
}
